import { useState, useEffect } from "react";
import AppHeader from "./AppHeader";

// ── Shop: in-app showcase that hands off to Shopify for checkout. ─────────────
// Sells only what is live on Shopify today (Home Hydration Hub, Alkaline WTR FLTR).
// Gen 2 is teased as coming soon, honoring the brand rule: Intelligent Faucet is
// bundled into the Home WTR Hub Gen 2 at 1,399.99, never a separate SKU. Live
// prices are deferred to Shopify so nothing is hardcoded that can drift; the only
// price stated is the canonical Gen 2 figure, read from config.
const C = {
  white:"#FFFFFF", bg:"#F8FAFB", border:"#E8EEF2",
  blue:"#51B0E6", blueDark:"#2A8FCA", blueLight:"#EDF6FC",
  body:"#1A2535", muted:"#6B7A8D", green:"#1E8A4C", amber:"#C89B3C",
};

const API_BASE = 'https://generosity-sales-engine-mvp-api.onrender.com';
const STORE_URL = 'https://generositywtr.myshopify.com';
const HUB_URL = STORE_URL + '/products/home-hydration-hub';
const FILTER_URL = STORE_URL + '/products/alkaline-wtr-fltr';
const PROMO_CODE = 'WELCOME100'; // $100 off. TODO: move into shopify_promo config row for central control.

function SIcon({ name, size=22, color=C.blueDark }) {
  const s=size, ic=color;
  const icons = {
    hub: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M3 10l9-7 9 7v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V10Z" stroke={ic} strokeWidth="1.6" strokeLinejoin="round"/><path d="M12 13s-2.2 2.2-2.2 3.6A2.2 2.2 0 0 0 12 18.8a2.2 2.2 0 0 0 2.2-2.2C14.2 15.2 12 13 12 13Z" stroke={ic} strokeWidth="1.3" fill={`${ic}20`}/></svg>,
    filter: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M4 5h16l-6 7v6l-4 2v-8L4 5Z" stroke={ic} strokeWidth="1.6" strokeLinejoin="round"/></svg>,
    faucet: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M4 8h6V6a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2h2a2 2 0 0 1 2 2v2h-8v2" stroke={ic} strokeWidth="1.5" strokeLinejoin="round"/><path d="M10 12c0 3-3 4-3 7h6c0-3-3-4-3-7Z" stroke={ic} strokeWidth="1.5" strokeLinejoin="round"/></svg>,
    tag: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M3 12l9-9 9 9-9 9-9-9Z" stroke={ic} strokeWidth="1.6" strokeLinejoin="round"/><circle cx="12" cy="12" r="2" fill={ic}/></svg>,
  };
  return <span style={{display:"inline-flex",alignItems:"center",justifyContent:"center"}}>{icons[name]||icons.hub}</span>;
}

// Anchor as a real link; file is small so GitHub web UI will not drop the tag.
function Buy({ href, children, primary }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" style={{
      display:"flex", alignItems:"center", justifyContent:"center", gap:8,
      textDecoration:"none", padding:"13px 0", borderRadius:13, width:"100%",
      fontSize:14, fontWeight:800, fontFamily:"inherit",
      background: primary ? `linear-gradient(135deg,${C.blue},${C.blueDark})` : C.white,
      color: primary ? "#fff" : C.blueDark,
      border: primary ? "none" : `1.5px solid ${C.blue}`,
    }}>{children}</a>
  );
}

function ProductCard({ icon, title, desc, children, status }) {
  return (
    <div style={{background:C.white,borderRadius:18,border:`1.5px solid ${C.border}`,padding:"18px 16px",opacity: status==="soon"?0.92:1}}>
      <div style={{display:"flex",alignItems:"flex-start",gap:12,marginBottom:12}}>
        <div style={{width:46,height:46,borderRadius:13,flexShrink:0,background:C.blueLight,display:"flex",alignItems:"center",justifyContent:"center"}}>
          <SIcon name={icon} size={24}/>
        </div>
        <div style={{flex:1,minWidth:0}}>
          <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
            <span style={{fontSize:16,fontWeight:800,color:C.body}}>{title}</span>
            {status==="live" && <span style={{fontSize:9,fontWeight:800,color:C.green,background:`${C.green}14`,border:`1px solid ${C.green}30`,borderRadius:20,padding:"2px 8px",letterSpacing:0.5,textTransform:"uppercase"}}>In stock</span>}
            {status==="soon" && <span style={{fontSize:9,fontWeight:800,color:C.amber,background:`${C.amber}14`,border:`1px solid ${C.amber}30`,borderRadius:20,padding:"2px 8px",letterSpacing:0.5,textTransform:"uppercase"}}>Coming soon</span>}
          </div>
          <div style={{fontSize:12,color:C.muted,marginTop:4,lineHeight:1.5}}>{desc}</div>
        </div>
      </div>
      {children}
    </div>
  );
}

export default function ShopScreen({ onClose }) {
  const [gen2Price, setGen2Price] = useState(1399.99); // canonical Gen 2 bundle price; from config

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/api/wtr/config`);
        if (!r.ok) return;
        const j = await r.json();
        // sale_price is the canonical full price; app_price is the after-code figure.
        if (!cancelled && j && j.sale_price != null) setGen2Price(Number(j.sale_price));
      } catch(e) { /* keep canonical default */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const price = (n) => "$" + Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div data-testid="shop-screen" style={{fontFamily:"Nunito Sans,-apple-system,sans-serif",background:C.white,minHeight:"100%",color:C.body,display:"flex",flexDirection:"column",maxWidth:480,margin:"0 auto",WebkitFontSmoothing:"antialiased"}}>
      <link href="https://fonts.googleapis.com/css2?family=Nunito:wght@700;800;900&family=Nunito+Sans:wght@400;600;700&display=swap" rel="stylesheet"/>
      <style>{`@keyframes fadeIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}} *{box-sizing:border-box;-webkit-tap-highlight-color:transparent}`}</style>

      <AppHeader
        variant="modal"
        title="Shop"
        onClose={onClose}
      />

      <div style={{flex:1,overflowY:"auto",padding:"20px",display:"flex",flexDirection:"column",gap:16,paddingBottom:40,animation:"fadeIn 0.3s ease"}}>

        {/* Welcome offer banner */}
        <div style={{display:"flex",alignItems:"center",gap:12,background:C.blueLight,border:`1.5px solid ${C.blue}40`,borderRadius:16,padding:"14px 16px"}}>
          <SIcon name="tag" size={22} color={C.blueDark}/>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:14,fontWeight:800,color:C.body}}>New here? Take $100 off</div>
            <div style={{fontSize:12,color:C.muted,marginTop:2,lineHeight:1.4}}>
              Use code <span style={{fontWeight:800,color:C.blueDark,letterSpacing:0.5}}>{PROMO_CODE}</span> at checkout.
            </div>
          </div>
        </div>

        {/* LIVE: Home Hydration Hub */}
        <ProductCard
          icon="hub"
          title="Home Hydration Hub"
          status="live"
          desc="Multi-Stage filtration and live water intelligence at your tap. See current pricing on Shopify."
        >
          <Buy href={HUB_URL} primary>Shop the Hub</Buy>
        </ProductCard>

        {/* LIVE: Alkaline WTR FLTR */}
        <ProductCard
          icon="filter"
          title="Alkaline WTR FLTR"
          status="live"
          desc="Replacement alkaline filter, in stock now. See current pricing on Shopify."
        >
          <Buy href={FILTER_URL}>Shop the filter</Buy>
        </ProductCard>

        {/* COMING SOON: Home WTR Hub Gen 2 */}
        <ProductCard
          icon="faucet"
          title="Home WTR Hub Gen 2"
          status="soon"
          desc="Next-generation hub with the Intelligent Faucet included and Multi-Stage filtration. Launching soon."
        >
          <div style={{display:"flex",alignItems:"baseline",gap:8,paddingTop:2}}>
            <span style={{fontSize:22,fontWeight:900,color:C.body,fontFamily:"Nunito,sans-serif"}}>{price(gen2Price)}</span>
            <span style={{fontSize:12,color:C.muted,fontWeight:600}}>Intelligent Faucet included</span>
          </div>
        </ProductCard>

        {/* COMING SOON: replacement filters */}
        <ProductCard
          icon="filter"
          title="Replacement Filters"
          status="soon"
          desc="The full replacement-filter range is shipping into inventory now and will be available on Shopify shortly."
        />

        {/* Shop all */}
        <Buy href={STORE_URL} primary>Shop all on Shopify</Buy>

        <div style={{fontSize:11,color:C.muted,textAlign:"center",lineHeight:1.5,padding:"4px 8px"}}>
          Checkout is handled securely on Shopify. Prices and availability shown there are authoritative.
        </div>

      </div>
    </div>
  );
}
